import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText, Shield, DollarSign, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaTruckPickup, // For Roadside Assistance
  FaFileInvoiceDollar, // For Return to Invoice
  FaCogs, // For Consumables Cover
  FaGavel,
  FaRoad, // For Legal Compliance
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const RenaultTriberInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is insurance mandatory for Renault Triber?",
      answer: "Yes, at least third-party liability insurance is compulsory under Indian law.",
    },
    {
      question: "What is the average cost of Triber insurance?",
      answer: "Third-party plans cost around ₹3,000–₹4,000, while comprehensive plans range from ₹9,500–₹15,000 annually.",
    },
    {
      question: "Which policy is best for Renault Triber?",
      answer: "A comprehensive plan with zero depreciation and roadside assistance is highly recommended.",
    },
    {
      question: "Does Triber insurance cover engine damage due to floods?",
      answer: "Yes, but only if you opt for the engine protection add-on.",
    },
    {
      question: "Is zero-depreciation cover available for Triber?",
      answer: "Yes, it is available and highly useful for new cars.",
    },
    {
      question: "Can I transfer NCB from another car to Triber?",
      answer: "Yes, NCB is transferable.",
    },
    {
      question: "Can I renew Triber insurance online?",
      answer: "Yes, easily through insurer websites or TheFinMaster.",
    },
    {
      question: "What if my Triber insurance expires?",
      answer: "The policy lapses, NCB is lost, and inspection may be required for renewal.",
    },
    {
      question: "Are cashless repairs available for Renault Triber?",
      answer: "Yes, at insurer-approved garages.",
    },
    {
      question: "Does insurance cover theft of accessories?",
      answer: "Yes, if you opt for accessories cover.",
    },
    {
      question: "Does Triber insurance cover natural calamities?",
      answer: "Yes, comprehensive policies cover floods, earthquakes, and cyclones.",
    },
    {
      question: "Can NRIs insure a Renault Triber in India?",
      answer: "Yes, with a local nominee.",
    },
    {
      question: "Can I buy long-term insurance for Renault Triber?",
      answer: "Yes, multi-year third-party with 1-year own damage cover is available.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Full claim settlement without depreciation cuts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection Cover",
      description: "Covers engine damage from waterlogging or oil leakage.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Towing, jump-starts, tyre change, and emergency fuel supply.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers small consumables like lubricants, oils, and bolts.",
      icon: <FaCogs className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI) Cover",
      description: "Reimburses full invoice value in case of theft or total loss.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
    }),
  };

  const tableData = [
    {
      type: "Third-Party Only",
      premium: "₹3,000 – ₹4,000",
      color: "orange",
    },
    {
      type: "Comprehensive",
      premium: "₹9,500 – ₹15,000",
      color: "teal",
    },
    {
      type: "Comprehensive + Add-ons",
      premium: "₹12,000 – ₹18,000",
      color: "indigo",
    },
  ];

  const steps = [
    "Inform your insurer immediately after accident, theft, or damage.",
    "File an FIR in case of theft or major accident.",
    "Submit claim documents (policy copy, RC, DL, FIR if applicable, repair bills).",
    "A surveyor inspects the damages.",
    "Opt for cashless repair at authorized garages or reimbursement claim.",
  ];

  return (
    <>
      <Helmet>
        <title>Renault Triber Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Renault Triber car insurance options in India. Compare quotes, customize coverage, and protect your 7-seater MPV with the best plans."
        />
        <meta
          name="keywords"
          content="renault triber car insurance, 7-seater mpv insurance, budget mpv insurance, car insurance quotes"
        />
        <link rel="canonical" href="https://thefinmaster.com/renault-triber-car-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Renault Triber Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Renault Triber</span> Car Insurance Guide
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  The Renault Triber is a versatile 7-seater MPV designed for Indian families, offering a spacious cabin, modular seating, modern styling, and fuel efficiency at an affordable price point. Popular among budget-conscious buyers, the Triber provides a practical mix of comfort and utility. To safeguard your Renault Triber against risks like accidents, theft, or natural calamities, the right car insurance is essential. Insurance not only ensures legal compliance but also protects you financially from unexpected repair costs. This guide explains everything you need to know about Renault Triber car insurance, including policy types, premiums, add-ons, claim process, and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="/assets/triber-insurance.jpeg"
                alt="Renault Triber"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center justify-center mb-12">
            <span className="w-12 h-12 border-2 border-blue-500 rounded-lg flex items-center justify-center mr-3">
              <Shield className="text-blue-600 w-7 h-7" />
            </span>
            Why Car Insurance for Renault Triber is Important
          </h3>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-red-500 mb-4">
                <FaGavel className="w-6 h-6 text-red-600" />
              </div>
              <h4 className="text-lg font-semibold text-red-700 mb-2">Legal Compliance</h4>
              <p className="text-red-600 text-sm leading-relaxed">
                Third-party liability insurance is mandatory under the Motor Vehicles Act.
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-green-500 mb-4">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-green-700 mb-2">Financial Protection</h4>
              <p className="text-green-600 text-sm leading-relaxed">
                Covers repair costs, damages, and third-party liabilities.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-blue-500 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-blue-700 mb-2">Peace of Mind</h4>
              <p className="text-blue-600 text-sm leading-relaxed">
                Comprehensive policies protect against accidents, fire, theft, and natural disasters.
              </p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-indigo-500 mb-4">
                <ShieldCheck className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="text-lg font-semibold text-indigo-700 mb-2">Enhanced Coverage</h4>
              <p className="text-indigo-600 text-sm leading-relaxed">
                Add-ons like zero depreciation, roadside assistance, and engine protection provide stronger protection.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 mt-6 md:px-14">
        <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
          <motion.h3
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="text-2xl font-bold text-gray-800 mb-10 flex items-center"
          >
            <div className="w-10 h-10 border-2 border-purple-500 rounded-lg flex items-center justify-center mr-3">
              <FileText className="text-purple-600 w-6 h-6" />
            </div>
            Types of Renault Triber Car Insurance Policies
          </motion.h3>
          <div className="space-y-6">
            {[
              {
                id: 1,
                title: "Third-Party Liability Insurance",
                border: "orange",
                points: [
                  "Mandatory under Indian law.",
                  "Covers damages to third-party vehicles, property, or injuries/death.",
                  "Does not cover your own Renault Triber.",
                ],
              },
              {
                id: 2,
                title: "Comprehensive Car Insurance",
                border: "teal",
                points: [
                  "Covers both third-party liabilities and own damages.",
                  "Protection against theft, accidents, fire, and natural/man-made disasters.",
                  "Can be customized with add-ons for wider coverage.",
                ],
              },
              {
                id: 3,
                title: "Standalone Own Damage Insurance",
                border: "indigo",
                points: [
                  "Available if you already have third-party liability cover.",
                  "Protects your Triber from accidents, theft, or natural calamities.",
                ],
              },
            ].map((policy, i) => (
              <motion.div
                key={policy.id}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                whileHover={{ scale: 1.03, transition: { type: "spring", stiffness: 200 } }}
                className={`border-l-4 border-${policy.border}-500 bg-${policy.border}-50 p-6 rounded-r-xl shadow-sm hover:shadow-md transition`}
              >
                <div className="flex items-start space-x-4">
                  <div
                    className={`flex-shrink-0 w-8 h-8 border border-${policy.border}-500 rounded-full bg-white flex items-center justify-center text-${policy.border}-600 font-bold text-sm`}
                  >
                    {policy.id}
                  </div>
                  <div className="flex-1">
                    <h4 className={`text-lg font-semibold text-${policy.border}-700 mb-3`}>{policy.title}</h4>
                    <ul className={`space-y-2 text-${policy.border}-600 text-sm leading-relaxed`}>
                      {policy.points.map((point, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className={`w-1.5 h-1.5 bg-${policy.border}-500 rounded-full mt-2 mr-3`}></span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="px-6 mt-10 md:px-14">
        <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">
            Average Premium for Renault Triber Car Insurance
          </h3>
          <p className="text-gray-600 text-sm mb-8">
            Premiums vary depending on variant, city of registration, IDV (Insured Declared Value), and add-ons selected.
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="grid grid-cols-2 bg-gray-100 text-gray-700 font-semibold text-sm">
              <div className="p-4">Policy Type</div>
              <div className="p-4">Annual Premium (Approx.)</div>
            </div>
            {tableData.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-2 border-t border-gray-200 text-sm"
              >
                <div className={`p-4 font-medium text-${row.color}-700 flex items-center`}>
                  <span className={`w-2 h-2 rounded-full bg-${row.color}-500 mr-2`}></span>
                  {row.type}
                </div>
                <div className={`p-4 text-${row.color}-600 font-semibold`}>{row.premium}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="container mx-auto p-6 mt-10">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Add-On Covers for Renault Triber
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full px-6 md:px-14 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Factors Affecting Premium
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Variant and age of the Renault Triber</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Insured Declared Value (IDV)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>City/region of registration</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Type of policy chosen</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Add-ons purchased</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>No Claim Bonus (NCB)</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Renault Triber Insurance
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>RC (Registration Certificate) of the car</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance copy (if renewal)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN/Aadhaar for KYC</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-size photograph</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Driving license (for claim settlement)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 mt-10 md:px-14 mb-4">
        <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-10">
            Renault Triber Car Insurance Claim Process
          </h3>
          <div className="relative">
            <span className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-200"></span>
            <ul className="space-y-8">
              {steps.map((step, idx) => (
                <li key={idx} className="relative flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold z-10">
                    {idx + 1}
                  </span>
                  <div className="ml-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-4 flex-1">
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                      {step}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaRoad />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                The Renault Triber is an affordable, spacious, and family-friendly MPV that deserves complete financial protection.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Opting for a comprehensive insurance policy with key add-ons like zero depreciation and roadside assistance ensures peace of mind, safety, and legal compliance. With TheFinMaster, you can easily compare quotes, choose the right plan, and buy Renault Triber insurance online.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default RenaultTriberInsurance;